/**
 * Analytics Service - Centralized Google Analytics implementation
 * Handles GA4 tracking with privacy compliance and error handling
 */

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.initAttempted = false;
    this.config = {};
    this.ga4Config = {};
    this.debugMode = false;
    
    // Wait for DOM and config to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.delayedInit());
    } else {
      // If DOM is already ready, wait a bit for config to load
      setTimeout(() => this.delayedInit(), 100);
    }
  }

  /**
   * Delayed initialization to ensure config is loaded
   */
  delayedInit() {
    if (this.initAttempted) return;
    this.initAttempted = true;
    
    this.loadConfig();
    this.init();
  }

  /**
   * Load configuration from window.APP_CONFIG
   */
  loadConfig() {
    this.config = window.APP_CONFIG?.analytics || {};
    this.ga4Config = this.config.ga4 || {};
    this.debugMode = this.config.debug || false;
    
    // Special handling for localhost development
    this.isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    // Add debug log only if debug mode is enabled
    if (this.debugMode) {
      console.log('[Analytics] Config loaded:', {
        config: this.config,
        ga4Config: this.ga4Config,
        debugMode: this.debugMode,
        isLocalhost: this.isLocalhost,
        appConfigExists: !!window.APP_CONFIG
      });
    }
  }

  /**
   * Initialize Google Analytics
   */
  async init() {
    try {
      // Skip analytics on localhost unless explicitly enabled
      if (this.isLocalhost && !this.config.enableOnLocalhost) {
        this.createMockGtag(true); // silent = true (no verbose logs on localhost)
        this.isInitialized = true;
        return;
      }
      
      // Check if analytics is enabled and measurement ID is provided
      if (!this.ga4Config.enabled || !this.ga4Config.measurementId) {
        // Create silent mock implementation when analytics is disabled
        this.createMockGtag(true); // silent = true
        this.isInitialized = true;
        return;
      }

      // Check for cookie consent if required
      if (this.config.cookieConsent?.required && !this.hasConsent()) {
        return;
      }

      await this.loadGoogleAnalytics();
      this.configureGoogleAnalytics();
      this.isInitialized = true;
      
      // Track initial page view
      this.trackPageView();
      
    } catch (error) {
      console.error('[Analytics] Failed to initialize Google Analytics:', error);
      // Fallback to mock mode on error
      if (this.isLocalhost) {
        this.createMockGtag(false); // verbose on localhost
        this.isInitialized = true;
      }
    }
  }

  /**
   * Load Google Analytics script
   */
  async loadGoogleAnalytics() {
    return new Promise((resolve, reject) => {
      // Check if gtag is already loaded
      if (window.gtag) {
        resolve();
        return;
      }

      // Create the Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.ga4Config.measurementId}`;
      
      // Set referrer policy for localhost
      if (this.isLocalhost) {
        script.referrerPolicy = 'no-referrer-when-downgrade';
        script.crossOrigin = 'anonymous';
      }
      
      script.onload = () => {
        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
          window.dataLayer.push(arguments);
        };
        
        window.gtag('js', new Date());
        resolve();
      };
      
      script.onerror = (error) => {
        console.error('[Analytics] Failed to load Google Analytics script:', error);
        reject(new Error('Failed to load Google Analytics script'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Configure Google Analytics with privacy settings
   */
  configureGoogleAnalytics() {
    const config = {
      ...this.ga4Config.config,
      debug_mode: this.debugMode || this.isLocalhost
    };

    // For localhost, add additional debug settings
    if (this.isLocalhost) {
      config.send_page_view = false; // We'll send manually for better control
      config.transport_type = 'beacon';
    }

    // Configure consent mode if enabled
    if (this.config.cookieConsent?.consentMode) {
      window.gtag('consent', 'default', {
        ad_storage: 'denied',
        analytics_storage: this.hasConsent() ? 'granted' : 'denied',
        wait_for_update: 500,
      });
    }

    // Initialize GA4
    window.gtag('config', this.ga4Config.measurementId, config);
  }

  /**
   * Create mock gtag for localhost testing or when analytics is disabled
   */
  createMockGtag(silent = false) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      const args = Array.from(arguments);
      window.dataLayer.push(args);
    };
    
    window.gtag('js', new Date());
  }

  /**
   * Track page views
   */
  trackPageView(pagePath, pageTitle) {
    if (!this.isInitialized) return;

    try {
      window.gtag('event', 'page_view', {
        page_path: pagePath || window.location.pathname,
        page_title: pageTitle || document.title,
        page_location: window.location.href
      });
      
      this.log('Page view tracked:', { pagePath, pageTitle });
    } catch (error) {
      console.error('Failed to track page view:', error);
    }
  }

  /**
   * Track custom events
   */
  trackEvent(eventName, parameters = {}) {
    if (!this.isInitialized) return;

    try {
      window.gtag('event', eventName, parameters);
      this.log('Event tracked:', eventName, parameters);
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  /**
   * Track user interactions
   */
  trackUserInteraction(action, category = 'user_interaction', label = '') {
    this.trackEvent(action, {
      event_category: category,
      event_label: label,
      value: 1
    });
  }

  /**
   * Track form submissions
   */
  trackFormSubmission(formName, success = true) {
    this.trackEvent('form_submit', {
      form_name: formName,
      success: success,
      event_category: 'form_interaction'
    });
  }

  /**
   * Track CRM-specific events
   */
  trackCRMEvent(action, entityType, entityId = null) {
    this.trackEvent('crm_action', {
      action: action,
      entity_type: entityType,
      entity_id: entityId,
      event_category: 'crm_interaction'
    });
  }

  /**
   * Track authentication events
   */
  trackAuthEvent(action, method = 'email') {
    this.trackEvent('auth_action', {
      action: action,
      method: method,
      event_category: 'authentication'
    });
  }

  /**
   * Track navigation events
   */
  trackNavigation(from, to, method = 'click') {
    this.trackEvent('navigation', {
      from: from,
      to: to,
      method: method,
      event_category: 'navigation'
    });
  }

  /**
   * Update consent status
   */
  updateConsent(granted = true) {
    if (!window.gtag) return;

    window.gtag('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied'
    });

    // Store consent in localStorage
    localStorage.setItem('analytics_consent', granted ? 'granted' : 'denied');
    
    // Reinitialize if consent was granted
    if (granted && !this.isInitialized) {
      this.init();
    }

    this.log('Consent updated:', granted);
  }

  /**
   * Check if user has given consent
   */
  hasConsent() {
    if (!this.config.cookieConsent?.required) return true;
    return localStorage.getItem('analytics_consent') === 'granted';
  }

  /**
   * Set user properties
   */
  setUserProperties(properties) {
    if (!this.isInitialized) return;

    try {
      window.gtag('config', this.ga4Config.measurementId, {
        user_properties: properties
      });
      this.log('User properties set:', properties);
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  /**
   * Set user ID for cross-device tracking
   */
  setUserId(userId) {
    if (!this.isInitialized) return;

    try {
      window.gtag('config', this.ga4Config.measurementId, {
        user_id: userId
      });
      this.log('User ID set:', userId);
    } catch (error) {
      console.error('Failed to set user ID:', error);
    }
  }

  /**
   * Log debug messages
   */
  log(message, ...args) {
    if (this.debugMode || window.APP_CONFIG?.features?.enableDebugMode) {
      console.log('[Analytics]', message, ...args);
    }
  }

  /**
   * Get initialization status
   */
  isReady() {
    return this.isInitialized;
  }

  /**
   * Manually reinitialize (useful for consent changes)
   */
  async reinitialize() {
    this.isInitialized = false;
    this.initAttempted = false;
    this.loadConfig();
    await this.init();
  }

  /**
   * Force initialization if not already done
   */
  async ensureInitialized() {
    if (!this.isInitialized && !this.initAttempted) {
      this.delayedInit();
    }
    
    // Wait up to 2 seconds for initialization
    let attempts = 0;
    while (!this.isInitialized && attempts < 20) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }
    
    return this.isInitialized;
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;
